"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntersectionObserver } from '../foundation/intersection-observer';
import "./countup.css";
/**
 * CountUp Component - Animates a count from a starting number to an ending number over a specified duration.
 * @param id - The ID for the count element.
 * @param pre - Text to display before the count.
 * @param post - Text to display after the count.
 * @param start - The starting number of the count.
 * @param end - The ending number of the count.
 * @param duration - The duration of the count in milliseconds.
 * @param decimals - Number of decimal places to display.
 * @param content - Additional content to display below the count.
 */
CountUp.propTypes = {
    id: PropTypes.string.isRequired,
    pre: PropTypes.string,
    post: PropTypes.string,
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    decimals: PropTypes.number,
    content: PropTypes.string,
};
export function CountUp({ id, pre = '', post = '', start, end, duration, decimals = 0, content }) {
    const decimalize = (val) => decimalPlaces > 0 ? val.toFixed(decimalPlaces) : Math.floor(val).toString();
    const decimalPlaces = decimals ?? 0;
    const [counter, setCounter] = useState(decimalize(start));
    const intervalMs = 16; // ~60fps
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useIntersectionObserver((entry) => {
        if (entry.isIntersecting)
            setIsVisible(true);
    }, {
        rootMargin: "0px 0px -100px 0px", // same as scrollfade
        threshold: 0, // same as scrollfade
        disconnectAfterIntersection: true
    } // fires once
    );
    useEffect(() => {
        if (!isVisible)
            return;
        const steps = Math.max(1, Math.round(duration / intervalMs));
        const step = (end - start) / steps;
        let current = start;
        const timer = setInterval(() => {
            current += step;
            const done = (step >= 0) ? current >= end : current <= end;
            if (done) {
                setCounter(decimalize(end));
                clearInterval(timer);
            }
            else {
                setCounter(decimalize(current));
            }
        }, intervalMs);
    }, [isVisible]);
    return (_jsx(_Fragment, { children: _jsxs("div", { ref: containerRef, className: "countup", suppressHydrationWarning: true, children: [_jsx("span", { className: 'countup-pre', children: pre }), _jsx("span", { id: id, className: 'countup-counter', children: counter }), _jsx("span", { className: 'countup-post', children: post }), _jsx("p", { children: content })] }) }));
}
